import React, {
	Dispatch,
	memo,
	useCallback,
	useMemo,
	useState,
	useEffect,
} from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Client, Modal } from "../../../../services";
import { globalState } from "../../../../redux/reducers";
import { useTypedSelector } from "../../../../redux/store";
import { useTaxiServiceFilterAccessControl } from "../../../../hooks";
import MultiSelectWithModalBase, {
	MultiSelectWithModal,
} from "../MultiSelectWithModal";
import { StyledP, StyledRow, StyledColumn } from "../..";

const EqualWidthColumn = styled(StyledColumn)`
	flex: 1;
`;

const MultiSelectClientWithModal: React.FC<
	MultiSelectClientWithModal.Props
> = ({
	value = [],
	onChange,
	disabled,
	titleText,
	required,
	modalSetting,
	sort,
	subscribeOptions = {},
	taxiServiceIds,
	isOnCheckBox = true,
}) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const { accessTaxiServicesIds } = useTaxiServiceFilterAccessControl<any>();

	const lang = useTypedSelector((state) => state.session.language);
	const items = useTypedSelector(
		(state) => state.globalState.multySelectModals.data.request.clients,
	);

	const [valueSearch, saveSearch] = useState<string>("");
	const [error, setError] = useState("");
	const [items2, setItems] = useState<
		Modal.ModelSimpleForMultySelectModalClient[]
	>([]);
	const [loading, setLoading] = useState(false);
	const [offset, setOffset] = useState<number>(0);
	const [limit, setLimit] = useState<number>(50);
	const [visibleCount, setVisibleCount] = useState<number>(limit);

	useEffect(() => {
		//* Wait until the data from Redux is available
		if (items.length === 0) return;

		//* Prevent re-running this effect logic, allowing the main smooth pagination logic to take over
		if (visibleCount > limit) return;

		//* Ensure that the initial render displays all items corresponding to already selected checkboxes
		if (items.length > limit && value.length !== 0)
			setVisibleCount(items.length);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [items]);

	const texts = useMemo(
		() => ({
			title: t("clientChoise"),
			allText: t("all_client"),
			allActiveText: t("all_active_client"),
		}),
		[t],
	);

	const optionsService = useMemo<
		Omit<Modal.SubscribeOptions, "typedb">
	>(() => {
		const result: Omit<Modal.SubscribeOptions, "typedb"> = {
			lang,
			taxiServiceIds: taxiServiceIds || accessTaxiServicesIds,
			...(valueSearch && valueSearch.length >= 2
				? { query: valueSearch, limit }
				: { limit, offset }),
			...subscribeOptions,
		};
		return result;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		accessTaxiServicesIds,
		lang,
		valueSearch,
		taxiServiceIds,
		limit,
		offset,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		JSON.stringify(subscribeOptions),
	]);

	useEffect(() => {
		if (!isOnCheckBox) return () => {};

		let mounted = true;
		const requestId = Date.now();

		const loadData = async () => {
			setLoading(true);
			try {
				const currentId = requestId;
				const data = await Modal.getAllClient(optionsService);

				if (!mounted || currentId !== requestId) return;

				const combined = [...items, ...(data?.items ?? [])];
				const unique = Array.from(
					new Map(combined.map((item) => [item.id, item])).values(),
				);

				if (valueSearch && valueSearch.length >= 2) {
					setVisibleCount(unique.length + 5);
				}

				dispatch(
					globalState.multySelectModals.data.setRequestClients(
						unique,
					),
				);

				// setItems((prev) => {
				// 	const combined = [...prev, ...(data?.items ?? [])];
				// 	const unique = Array.from(
				// 		new Map(
				// 			combined.map((item) => [item.id, item]),
				// 		).values(),
				// 	);

				// 	if (valueSearch && valueSearch.length >= 2)
				// 		setVisibleCount(unique.length + 5);
				// 	return unique;
				// });
			} catch (err) {
				console.error("Failed to fetch Modal.getAll:", err);
				// if (mounted) setItems([]);
				if (mounted)
					dispatch(
						globalState.multySelectModals.data.setRequestClients(
							[],
						),
					);
			} finally {
				if (mounted) setLoading(false);
			}
		};

		loadData();

		return () => {
			mounted = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOnCheckBox, optionsService]);

	const convertDataToFullname = (
		client: Modal.ModelSimpleForMultySelectModalClient,
	): string => {
		const defaultTitle = "Unknown";

		const lastName = client?.lastName ? client?.lastName : "";
		const firstName = client?.firstName ? client?.firstName : "";
		const fatherName = client?.fatherName ? client?.fatherName : "";

		const fullName = `${lastName} ${firstName} ${fatherName}`.trim();

		const clientName = fullName.length > 0 ? fullName : defaultTitle;

		const clientPhone = client?.phone ? client?.phone : defaultTitle;

		const retval = `${clientPhone} (${clientName})`;

		return retval;
	};

	const selectOptions = useMemo<MultiSelectWithModalBase.Value>(
		() =>
			items
				.slice(0, visibleCount)
				.map((item) => ({
					key: item.id,
					label: convertDataToFullname(item),
					value: item.id,
					name: convertDataToFullname(item),
					data: item,
				}))
				.filter((option) => option.label !== ""),
		[items, visibleCount],
	);

	const selected = useMemo<MultiSelectWithModalBase.Value>(
		() =>
			selectOptions.filter((item) =>
				value.find((data) => data === item.value),
			),

		[selectOptions, value],
	);

	const selectOnChange = useCallback(
		(newValue) => {
			setError("");
			const ids = newValue.map((item) => item.value);
			onChange(ids);
		},
		[onChange],
	);

	useEffect(() => {
		if (disabled) {
			onChange([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [disabled]);

	const handleScroll = useCallback(
		(event: number) => {
			if (event + 5 >= visibleCount) {
				setVisibleCount((prev) => prev + 5);
			}
			if (event + 20 >= items.length && items.length >= offset) {
				setOffset((prev) => prev + limit);
			}
		},
		[items.length, limit, offset, visibleCount],
	);

	return (
		<StyledRow gap="7px" alignItems="center">
			<EqualWidthColumn gap="7px">
				{titleText && <StyledP>{t(titleText) ?? ""}</StyledP>}
				<MultiSelectWithModal
					value={selected}
					onChange={selectOnChange}
					options={selectOptions}
					error={error}
					disabled={disabled}
					title={texts.title}
					allText={texts.allText}
					allActiveText={texts.allActiveText}
					required={required}
					pullUpItemInsideArray
					hiddenButton={false}
					showSelectAll
					modalSetting={modalSetting}
					// sort={{
					// 	show: true,
					// 	active: true,
					// 	...(sort || {}),
					// }}
					// loadingList={loading}
					valueSearch={valueSearch}
					saveSearch={saveSearch}
					onScrollMaxVisible={handleScroll}
				/>
			</EqualWidthColumn>
		</StyledRow>
	);
};

declare namespace MultiSelectClientWithModal {
	interface Props extends MultiSelectWithModalBase.Setting {
		value: Value;
		onChange: Dispatch<Value>;
		titleText?: string | string[];
		modalSetting?: MultiSelectWithModalBase.ListSelectProps["modalSetting"];
		subscribeOptions?: Client.SubscribeOptions;
		taxiServiceIds?: number[];
		isOnCheckBox?: boolean;
	}

	type Value = number[];
}

export default memo(MultiSelectClientWithModal);
